import { useCallback, useEffect, useRef, useState } from "react";
import { printAgent } from "@/lib/api";

/**
 * A ponte de impressão está de pé nesta máquina?
 *
 * O defeito que isto existe para evitar: a venda fecha, o agente está fechado,
 * e o cupom não sai. Hoje o PDV silencia esse caso de propósito (ver
 * `printReceipt`, modo silencioso) — e silenciar é a decisão certa para o
 * tablet que nunca teve impressora, mas é a errada para o balcão que tem.
 * No balcão, a primeira pista de que o agente caiu era o cliente esperando o
 * papel que não vem.
 *
 * A distinção que resolve os dois: `jaRespondeu`. Se a ponte respondeu ALGUMA
 * VEZ nesta máquina, ela é esperada aqui — e o silêncio vira aviso. Se nunca
 * respondeu, esta máquina simplesmente não imprime, e avisar seria ruído a
 * cada venda.
 *
 * `jaRespondeu` fica no `localStorage` porque a pergunta é sobre a MÁQUINA, não
 * sobre a aba: fechar o navegador não faz o balcão deixar de ter impressora.
 *
 * A sondagem é espaçada (20s) e silenciosa: `printAgent.health()` já devolve
 * `null` em vez de lançar, e um erro de rede a cada 20s no console do balcão
 * seria barulho sem leitor.
 */
const CHAVE = "osp_print_agent_visto";
const INTERVALO_MS = 20_000;

export default function useAgenteDeImpressao({ ativo = true } = {}) {
  const [saude, setSaude] = useState(null);
  const [verificando, setVerificando] = useState(false);
  const [jaRespondeu, setJaRespondeu] = useState(() => {
    try { return localStorage.getItem(CHAVE) === "1"; } catch { return false; }
  });
  // Evita que uma resposta atrasada sobrescreva o estado depois de desmontar.
  const vivo = useRef(true);

  const verificar = useCallback(async () => {
    setVerificando(true);
    const r = await printAgent.health();
    if (!vivo.current) return r;
    setSaude(r);
    setVerificando(false);
    if (r) {
      setJaRespondeu(true);
      try { localStorage.setItem(CHAVE, "1"); } catch { /* aba anônima: segue sem lembrar */ }
    }

    return r;
  }, []);

  useEffect(() => {
    vivo.current = true;
    if (!ativo) return undefined;
    verificar();
    const t = setInterval(verificar, INTERVALO_MS);

    return () => { vivo.current = false; clearInterval(t); };
  }, [ativo, verificar]);

  return {
    saude,
    online: !!saude,
    jaRespondeu,
    verificando,
    /* "Esta máquina deveria estar imprimindo e não está." É o único estado que
       merece ocupar espaço na tela do balcão. */
    caiu: jaRespondeu && !saude && !verificando,
    verificar,
  };
}
